import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { COOKIE_DOMAIN } from '../../../constants/utils';
import { ApplicationConfig } from '../../../utils/config/config';

@Injectable({
  providedIn: 'root'
})
export class CookieHelperService {
  private applicationConfig = inject(ApplicationConfig);
  private platformId = inject(PLATFORM_ID);

  setCookies(cookieName: string, cookieValue: unknown): void {
    if (isPlatformBrowser(this.platformId)) {
      const { hostname } = window.location;
      const cookiesLife = new Date();
      cookiesLife.setTime(cookiesLife.getTime() + 365 * 24 * 60 * 60 * 1000);
      const expires = '; expires=' + cookiesLife.toUTCString();
      const cookieDomain =
        hostname === 'localhost'
          ? 'localhost'
          : this.applicationConfig.get('APP_COOKIE_DOMAIN');
      const cookieDetails =
        cookieName + '=' + cookieValue + expires + cookieDomain;
      document.cookie = cookieDetails;
    }
  }

  setDeepLinkCookie(cookieName: string, cookieValue: unknown): void {
    if (isPlatformBrowser(this.platformId)) {
      const cookiesLife = new Date();
      // set for 30 min
      cookiesLife.setTime(cookiesLife.getTime() + 30 * 60 * 1000);
      const expires = '; expires=' + cookiesLife.toUTCString();
      const cookieDomain = this.applicationConfig.get('APP_COOKIE_DOMAIN');
      const cookieDetails =
        cookieName + '=' + cookieValue + expires + cookieDomain;
      document.cookie = cookieDetails;
    }
  }

  setCookieName(
    cookieName: string,
    cookieValue: string | boolean | number,
    expireDays: number,
    keepSession: boolean,
    pathValue = '',
    domainValue?: string
  ): void {
    const cookiesLife = new Date();
    if (expireDays > 0 || keepSession) {
      cookiesLife.setTime(
        cookiesLife.getTime() + expireDays * 24 * 60 * 60 * 1000
      );

      const expires = keepSession
        ? ''
        : '; expires=' + cookiesLife.toUTCString();
      const path = this._getCookiePath(pathValue);
      let cookieDetails = cookieName + '=' + cookieValue + expires + path;
      cookieDetails = this._getCookieDetails(domainValue, cookieDetails);
      document.cookie = cookieDetails;
    }
  }

  private _getCookieDetails(
    domainValue: string | undefined,
    cookieDetails: string
  ): string {
    const { hostname } = window.location;

    return domainValue
      ? `${cookieDetails};domain=${
          hostname === 'localhost' ? 'localhost' : domainValue
        }`
      : cookieDetails;
  }

  private _getCookiePath(pathValue: string): string {
    let path = '';
    if (pathValue) {
      path = `;path=${pathValue}`;
    }
    return path;
  }

  getCookieValue(cookieName: string): string | null {
    const result = new RegExp(
      '(?:^|; )' + encodeURIComponent(cookieName) + '=([^;]*)'
    ).exec(document.cookie);
    return result ? result[1] : null;
  }

  deleteCookie(cookieName: string) {
    const date = new Date();
    // Set it expire in -1 days
    date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000);
    // Set it
    document.cookie =
      cookieName + '=; expires=' + date.toUTCString() + COOKIE_DOMAIN;
  }

  getAccountCookie(cookieName: string): string {
    const name = cookieName + '=';
    const temp = document.cookie.split(';');
    for (let i = 0; i < temp.length; i++) {
      let ca = temp[i];
      while (ca.charAt(0) === ' ') {
        ca = ca.substring(1);
      }
      if (ca.indexOf(name) === 0) {
        return ca.substring(name.length, ca.length);
      }
    }
    return '';
  }

  getCountryCode(): string {
    let countryCode = '';
    const prefCookie = this.getCookieValue('prefUI');
    if (prefCookie) {
      countryCode = prefCookie.split('-')[1]?.toLowerCase();
    }
    return countryCode;
  }

  /**
   * Generate a unique ID (random + current timestamp)
   *
   * @returns {string} Value conformed by random string/numbers + current timestamp
   * @memberof CookieHelper
   */
  generateUID(): string {
    function generateNumber(): string {
      return Math.floor((1 + Math.random()) * 0x10000000)
        .toString(16)
        .substring(1);
    }
    return `${generateNumber()}-${Date.now()}-${generateNumber()}`;
  }
}
